import type { InventoryItem, Project, ProjectNeedle, NeedleKind } from './types';

/**
 * Canonical mm → US needle-size table used by the size picker and the
 * inventory-side inference. Single source of truth — `ProjectForm` and
 * `NeedleEditor` both consume this constant.
 */
export const NEEDLE_DATA: { mm: string; us: string }[] = [
  { mm: '2.0', us: 'US 0' },
  { mm: '2.25', us: 'US 1' },
  { mm: '2.5', us: 'US 1.5' },
  { mm: '2.75', us: 'US 2' },
  { mm: '3.0', us: 'US 2.5' },
  { mm: '3.25', us: 'US 3' },
  { mm: '3.5', us: 'US 4' },
  { mm: '3.75', us: 'US 5' },
  { mm: '4.0', us: 'US 6' },
  { mm: '4.5', us: 'US 7' },
  { mm: '5.0', us: 'US 8' },
  { mm: '5.5', us: 'US 9' },
  { mm: '6.0', us: 'US 10' },
  { mm: '6.5', us: 'US 10.5' },
  { mm: '7.0', us: 'US 10.75' },
  { mm: '8.0', us: 'US 11' },
  { mm: '9.0', us: 'US 13' },
  { mm: '10.0', us: 'US 15' },
  { mm: '12.0', us: 'US 17' },
  { mm: '15.0', us: 'US 19' },
  { mm: '18.0', us: 'US 35' },
];

/**
 * Normalize a needle size string ("4mm", "4.0 mm", " 4 MM ", "10.0mm",
 * "2,25mm") → "4" / "10" / "2.25". Parses the numeric part so "10mm",
 * "10.0mm" and "10 mm" all collapse to the same canonical key. Czech
 * decimal commas ("2,25mm", "2,5mm") are converted to dots before
 * parsing — without this, `parseFloat` stops at the comma and reads
 * both "2,25" and "2,5" as `2`, collapsing distinct sizes onto each
 * other and onto "2.0mm". Veronika 2026-05-19 (Telegram msg 4102): her
 * inventory's "2,25mm" + "2,5mm" ChiaoGoo needles were both wrongly
 * flagged as "in use by 1977 SOCKS", which only uses 2.0mm. Falls back
 * to the stripped string if no number is found (e.g. US sizes like
 * "US 6" → "us6").
 */
export const normSize = (s?: string): string => {
  const raw = (s || '')
    .replace(/\s/g, '')
    .replace(/mm$/i, '')
    .toLowerCase()
    // Global replace — a non-global `.replace(',', '.')` would only
    // convert the first comma, leaving any later one to break parseFloat.
    .replace(/,/g, '.');
  if (!raw) return '';
  const num = parseFloat(raw);
  if (Number.isFinite(num)) {
    // Use the numeric value so "10" and "10.0" become identical.
    // Trailing zeros after the decimal are stripped by toString().
    return num.toString();
  }
  return raw;
};

/**
 * Normalize a cable/needle length string ("40cm", "40 CM", "40 cm") → "40cm".
 */
export const normLen = (s?: string): string =>
  (s || '').replace(/\s/g, '').toLowerCase();

/**
 * Infer a NeedleKind from legacy inventory data. Used for one-time migration
 * of items created before the needleKind field existed.
 */
export function inferNeedleKindFromInventory(item: InventoryItem): NeedleKind {
  if (item.needleKind) return item.needleKind;
  if (item.type !== 'needle') return 'straight';
  const notes = (item.notes || '').toLowerCase();
  if (notes.includes('interchangeable')) return 'interchangeable-tip';
  if (notes.includes('circular')) return 'fixed-circular';
  return 'straight';
}

/**
 * Extract the attached cable length from a legacy inventory item whose kind
 * is fixed-circular — the cable was historically encoded in the notes string
 * as "Circular (fixed, cable: 40cm)".
 */
export function inferCableLengthFromNotes(notes?: string): string | undefined {
  if (!notes) return undefined;
  const m = notes.match(/cable:\s*([^),]+)/i);
  return m ? m[1].trim() : undefined;
}

/**
 * Infer a NeedleKind from a legacy ProjectNeedle entry (pre-needleKind).
 */
export function inferNeedleKindFromProject(n: ProjectNeedle): NeedleKind {
  if (n.needleKind) return n.needleKind;
  if (n.isInterchangeable) return 'interchangeable-tip';
  if (n.isCircular) return 'fixed-circular';
  return 'straight';
}

/**
 * Build a signature that identifies a *physical* needle object. Two needles
 * with the same signature and kind are the same physical object and therefore
 * cannot be used simultaneously on different projects.
 *
 *   straight             → "size|length"
 *   fixed-circular       → "size|cable"
 *   interchangeable-tip  → "size|tip-length"
 *
 * The tip-length component on interchangeable-tip was added 2026-05-19 after
 * Veronika msg 4118-4120: she owns 4.5mm tips with 8cm shafts AND 4.5mm tips
 * with 13cm shafts. They're physically different needles — she can use the
 * 8cm pair on the "Scrunchie" project and still have the 13cm pair free for
 * something else. The pre-2026-05-19 collapse-all-tips-of-same-size rule
 * (`tip|${s}`) was incorrect on this dimension; cables remain universal so
 * cableLength is NOT part of the signature (which is what `lenOrCable` here
 * actually carries for interchangeable-tip — `projectNeedleSignature` and
 * `inventoryNeedleSignature` both pass the tip-length field, not the cable).
 */
export function needleSignature(kind: NeedleKind, size: string, lenOrCable: string): string {
  const s = normSize(size);
  const l = normLen(lenOrCable);
  if (kind === 'interchangeable-tip') return `tip|${s}|${l}`;
  if (kind === 'fixed-circular') return `fc|${s}|${l}`;
  return `str|${s}|${l}`;
}

/**
 * Signature for an inventory item. Returns null if the item is not a needle.
 */
export function inventoryNeedleSignature(item: InventoryItem): string | null {
  if (item.type !== 'needle') return null;
  const kind = inferNeedleKindFromInventory(item);
  const lenOrCable =
    kind === 'fixed-circular'
      ? item.cableLength || inferCableLengthFromNotes(item.notes) || ''
      : item.length || '';
  return needleSignature(kind, item.size || '', lenOrCable);
}

/**
 * Signature for a project's needle entry.
 */
export function projectNeedleSignature(n: ProjectNeedle): string {
  const kind = inferNeedleKindFromProject(n);
  const lenOrCable =
    kind === 'fixed-circular'
      ? n.cableLength || ''
      : n.needleLength || '';
  return needleSignature(kind, n.size, lenOrCable);
}

export interface NeedleUsageEntry {
  projectId: number;
  projectName: string;
  status: 'in-progress' | 'hibernating' | 'future';
}

/**
 * Build a map of needle signature → list of active projects using that needle.
 * Finished projects are excluded — they no longer claim the physical object.
 */
export function buildNeedleUsageMap(
  projects: Project[],
  excludeProjectId?: number,
): Map<string, NeedleUsageEntry[]> {
  const map = new Map<string, NeedleUsageEntry[]>();
  for (const p of projects) {
    if (p.id === excludeProjectId) continue;
    if (p.status === 'finished') continue;
    if (p.status !== 'in-progress' && p.status !== 'hibernating' && p.status !== 'future') continue;
    for (const n of p.needles || []) {
      if (!n.size) continue;
      const sig = projectNeedleSignature(n);
      const list = map.get(sig) || [];
      list.push({ projectId: p.id!, projectName: p.name, status: p.status });
      map.set(sig, list);
    }
  }
  return map;
}

/**
 * Given an inventory item and the usage map, return how many physical copies
 * of this item are currently claimed by active projects.
 */
export function countClaims(
  item: InventoryItem,
  usage: Map<string, NeedleUsageEntry[]>,
): { claimed: number; projects: NeedleUsageEntry[] } {
  const sig = inventoryNeedleSignature(item);
  if (!sig) return { claimed: 0, projects: [] };
  const projects = usage.get(sig) || [];
  return { claimed: projects.length, projects };
}

/**
 * Availability of an inventory needle given its quantity and current claims.
 *   available  — claimed < quantity (there's at least one free copy)
 *   in-use     — claimed >= quantity (all copies are busy right now)
 *   conflict   — claimed > quantity (legacy data bug — user needs to resolve)
 */
export type AvailabilityState = 'available' | 'in-use' | 'conflict';

export function itemAvailability(
  item: InventoryItem,
  usage: Map<string, NeedleUsageEntry[]>,
): { state: AvailabilityState; claimed: number; quantity: number; projects: NeedleUsageEntry[] } {
  const { claimed, projects } = countClaims(item, usage);
  const quantity = Math.max(1, item.quantity || 1);
  let state: AvailabilityState = 'available';
  if (claimed > quantity) state = 'conflict';
  else if (claimed >= quantity) state = 'in-use';
  return { state, claimed, quantity, projects };
}
